import React, { useEffect, useState } from "react";
import { Formik, Field, FieldArray, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppInput from "components/Input/AppInput";
import { DAYS_OF_WEEK, SHIFT, BILLING_TYPE, MASK, JOB_TYPE, POST_JOB, BOOKING_STATUS, WORKER_TYPE, SYSTEM_ROLE, JOB_STATUS } from "config/AppConfig";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { YupValidations } from "utils/validate";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import CustomDatePicker from "components/Input/CustomDatePicker";
import { useParams } from "react-router";
import moment from "moment-timezone";
import AppRadio from "components/Input/AppRadio";
import SelectField from "components/Select/SelectField";
import {
  fetchSpecialties,
  getSpecialties,
  fetchSupports,
  getSupports,
  fetchAccreditation,
} from "store/slices/worker";
import {
  getWorkerApplied,
  getJobDetail,
  jobRemoveWorker,
  reduceCreateJob,
  // reduceUpdateJob,
  fetchJobDetail,
} from "store/slices/job";
import { fetchAllParticipants } from "store/slices/coordinatorParticipant";
import { autoBooking } from "store/slices/booking";
import { getAuthInfo } from "store/slices/auth";
import { fetchClientDetail } from "store/slices/client";

import { useDispatch, useSelector } from "react-redux";
import AppDialog from "components/Dialog/AppDialog";
import SearchWorkerModal from "./SearchWorkerModal";
import JobUserTag from "./JobUserTag";
import BookingWorkerModal from "./BookingWorkerModal";

import CalendarIcon from "assets/icons/calendar-color.svg";
import WorkerCalendarModal from "./WorkerCalendarModal";
import { useHistory } from "react-router-dom";
import { Label } from "reactstrap";
import SelectTime from "components/Select/SelectTime";
import classNames from "classnames";
import {
  fetchProcessNotePage,
  getProcessNotePage,
} from "store/slices/processNote";

import ProcessNoteContent from "./ProcessNote";


const defaultWorkHistory = {
  dayOfWeek: DAYS_OF_WEEK[0],
  shiftType: "",
  startTime: moment(new Date()).hours(0).minutes(0).format("YYYY/MM/DD HH:mm"),
  endTime: moment(new Date()).hours(0).minutes(0).format("YYYY/MM/DD HH:mm"),
  numberWorkers: "",
  isExpand: true,
};

const maskNumber = "999999999999999";

const useStyles = makeStyles((theme) => ({
  textarea: {
    height: "100px !important",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: "1rem",
  },
  radio: {
    color: AppColor.black,
    "& .MuiFormControlLabel-label": {
      fontSize: "1rem",
      fontWeight: 700,
    },
  },
  radioGroup: {
    flexDirection: "row",
  },
  postDescription: {
    border: "1px solid rgba(0, 0, 0, 0.2)",
    padding: "0.5rem 1rem",
    color: AppColor.black,
  },
  bold: {
    fontWeight: 700,
  },
  wrapShift: {
    border: "1px solid #c4b5bc",
    padding: "1rem",
    boxShadow: "0px 4px 4px rgb(0 0 0 / 25%)",
    borderRadius: 4,
    marginBottom: "0.5rem",
    "& .shiftInfo": {
      margin: "-1rem -1rem 1rem -1rem",
      padding: "0.3rem 1rem",
      border: "1px solid #c4b5bc",
      borderRadius: 4,
      backgroundColor: AppColor.white,
      display: "flex",
      justifyContent: "space-between",
    },
  },
  collapseShift: {
    border: "none",
    boxShadow: "none",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 1rem",
    margin: "0.5rem 0",
    "& .shiftInfo": {
      margin: 0,
      padding: "0.3rem 1rem",
      border: "1px solid #c4b5bc",
      borderRadius: 4,
      flex: "1 0 100%",
      backgroundColor: AppColor.white,
      display: "flex",
      justifyContent: "space-between",
    },
    "& .remove-icon": {
      fontSize: "2rem",
      color: AppColor.red,
    },
  },
  title: {
    display: "flex",
    margin: "2rem 0 1rem 0",
    "& h5": {
      color: AppColor.black,
    },
  },
  calendar: {
    cursor: "pointer",
  },
  label: {
    fontSize: 14,
    color: AppColor.black,
    fontWeight: "500",
  },
  timePicker: {
    display: "flex",
    "& .form-group": {
      flex: "1 0 0px",
    },
  },
  deleteRight: {
    cursor: "pointer",
  },
  disabled: {
    opacity: 0.6,
    pointerEvents: "none",
  },
  noteContent: {
    boxSizing: "border-box",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    backgroundColor: "#FFFFFF",
  },
  processNote: {
    width: "100%",
    height: "auto",
    border: "1px #000000 10%",
    padding: "20px",
  },
  icons: {
    cursor: "pointer",
    height: "49px",
    width: "49px",
    background: "#FFFFFF",
    border: "0.5px solid #000000",
    borderRadius: "24px",
  },
  listNote: {
    width: "100%",
    height: "auto",
    border: "1px #000000 10%",
    padding: "20px",
  },
  horizontalBar: {
    marginTop: "1rem",
    marginBottom: "1rem",
    border: "1px solid #cacaca",
    textAlign: "center",
    width: "95%",
    borderTop: "0",
  },
  customUserName: {
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#C78D3C",
  },
  customDayNote: {
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#000000",
  },
  textField: {
    width: "100%",
    "& .MuiInputBase-input": {
      height: "200px",
      verticalAlign: "top",
    },
  },
  paper: {
    marginRight: theme.spacing(2),
  },
}));

const JobForm = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { id: jobId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const authInfo = useSelector(getAuthInfo) || {};
  const processNote = useSelector(getProcessNotePage);
  const { role, clientId, companyId } = authInfo;
  let isHospital, isCoordinator;

  const isAdd = !jobId;

  const [workersApplied, setWorkersApplied] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [accreditations, setAccreditations] = useState([]);

  const [latLng, setLatLng] = useState();
  const [totalBooking, setTotalBooking] = useState(0);

  const [showSearchWorker, setShowSearchWorker] = useState(false);
  const [showBookingWorker, setShowBookingWorker] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [jobType, setJobType] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const [initializeData, setInitializeData] = useState({
    title: "",
    description: "",
    address1: "",
    address2: "",
    postalCode: "",
    department: "",
    floor: "",
    accreditationId: "",
    specialtyId: "",
    supportId: "",
    coordParticipantId: "",
    yearsOfExperience: "",
    startDate: "",
    endDate: "",
    jobAvailabilityRequests: [defaultWorkHistory],
    isPostJob: POST_JOB.POST_JOB,
    phone: "",
    status: '',

    planManagerEmail: "",
    planManagerOrganizationName: "",
    planManagerPhone: "",
    billToAddress: "",
    billingDetail: "",
  });
  const [clientDetail, setClientDetail] = useState({});

  switch (role) {
    case SYSTEM_ROLE.HOSPITAL_ADMIN:
    case SYSTEM_ROLE.HOSPITAL_OTHER:
      isHospital = true;
      break;
    case SYSTEM_ROLE.COORDINATOR_ADMIN:
    case SYSTEM_ROLE.COORDINATOR_OTHER:
      isCoordinator = true;
      break;
    default:
      break;
  }

  const { lat, lng } = latLng || {};

  const billingDetailsOptions = [
    {
      value: BILLING_TYPE.PLAN_MANAGER.id,
      label: t(BILLING_TYPE.PLAN_MANAGER.label),
    },
    {
      value: BILLING_TYPE.NDIA.id,
      label: t(BILLING_TYPE.NDIA.label),
    },
    {
      value: BILLING_TYPE.SELF.id,
      label: t(BILLING_TYPE.SELF.label),
    },
  ];
  const specialties = useSelector(getSpecialties) || [];
  const supports = useSelector(getSupports) || [];
  const jobDetail = useSelector(getJobDetail) || {};
  const specialtyOptions = specialties.map((item) => {
    return {
      value: item.id,
      label: item.name,
    };
  });
  const supportOptions = supports.map((item) => {
    return {
      value: item.id,
      label: item.name,
    };
  });

  const loadClientDetail = async () => {
    const result = await dispatch(fetchClientDetail({ clientId: clientId, companyId: companyId }));
    if (result.payload) {
      setClientDetail(result.payload);
    }
  };

  const _loadData = async () => {
    let participantList;
    if (isCoordinator) {
      const resultParticipants = await dispatch(fetchAllParticipants({clientId: clientId}));
      participantList = resultParticipants.payload || [];
      setParticipants(resultParticipants.payload);
    }

    const result = await dispatch(fetchJobDetail({ id: jobId }));
    const { payload } = result;
    if (payload) {
      const data = { ...initializeData };
      const {
        jobAvailabilities = [],
        startDate = "",
        endDate = "",
        isPostJob,
        totalBooking,
        specialty = {},
        accreditation = {},
        support = {},
        coordParticipant = {},
        client = {},
      } = payload;
      Object.keys(initializeData).forEach((key) => {
        data[key] = payload[key] || "";
      });
      data["jobAvailabilityRequests"] = jobAvailabilities.map((item) => {
        const clone = { ...item };
        let startTime, endTime, startHours, endHours, startMinutes, endMinutes;

        startHours = moment(item.startTime).hours();
        startMinutes = moment(item.startTime).minutes();
        endHours = moment(item.endTime).hours();
        endMinutes = moment(item.endTime).minutes();

        startTime = moment(new Date())
          .hours(startHours)
          .minutes(startMinutes)
          .format("YYYY/MM/DD HH:mm");
        endTime = moment(new Date())
          .hours(endHours)
          .minutes(endMinutes)
          .format("YYYY/MM/DD HH:mm");

        if (item.shiftType === "NIGHT") {
          if (startHours <= 6) {
            startTime = moment(startTime)
              .add(1, "day")
              .format("YYYY/MM/DD HH:mm");
          }
          if (endHours <= 6) {
            endTime = moment(endTime).add(1, "day").format("YYYY/MM/DD HH:mm");
          }
        }

        clone["startTime"] = startTime;
        clone["endTime"] = endTime;
        return clone;
      });
      if (client) {
        data["clientType"] = client.clientType;
        data["clientId"] = client.id;
        data["client"] = client;
      }
      data["specialtyId"] = specialty.id;
      if (isCoordinator && coordParticipant.id) {
        data["coordParticipantId"] = coordParticipant.id;
        const selectedParticipant = participantList.filter(
          (item) => item.id === coordParticipant.id
        )[0];
        if (selectedParticipant) {
          data["billingDetail"] = selectedParticipant.billingDetail;
          data["billToAddress"] = selectedParticipant.billToAddress;
          if (
            selectedParticipant.billingDetail === BILLING_TYPE.PLAN_MANAGER.id
          ) {
            data["planManagerOrganizationName"] =
              selectedParticipant.planManagerOrganizationName;
            data["planManagerPhone"] = selectedParticipant.planManagerPhone;
            data["planManagerEmail"] = selectedParticipant.planManagerEmail;
          }
        }
      }

      data["isPostJob"] = isPostJob ? POST_JOB.POST_JOB : POST_JOB.UNPOST_JOB;
      data["accreditationId"] = accreditation.id;
      data["supportId"] = support.id;
      data["startDate"] = moment(startDate).format("YYYY/MM/DD");
      data["endDate"] = moment(endDate).format("YYYY/MM/DD");
      
      setInitializeData(data);
      setJobType(payload.jobType);
      setTotalBooking(totalBooking);
    } else {
      history.push("/jobs");
    }
  };
  const loadSpecialties = async () => {
    await dispatch(fetchSpecialties());
  };
  const loadSupports = async () => {
    await dispatch(fetchSupports());
  };
  const loadAccreditations = async (workerType) => {
    const result = await dispatch(
      fetchAccreditation({ workerType: workerType })
    );
    if (result.payload) {
      setAccreditations(result.payload);
    }
  };
  const handleRemoveWorker = async ({ workerId }) => {
    await dispatch(
      jobRemoveWorker({
        jobId: jobId,
        workerId,
      })
    );
    loadWorkersApplied();
  };
  const loadWorkersApplied = async () => {
    const result = await dispatch(getWorkerApplied({ jobId: jobId }));
    if (result.payload) {
      setWorkersApplied(result.payload);
    }
  };
  const loadParticipants = async () => {
    const result = await dispatch(fetchAllParticipants({clientId: clientId}));
    if (result.payload) {
      setParticipants(result.payload);
    }
  };
  const handleAutoBooking = async () => {
    setIsLoading(true);
    await dispatch(autoBooking({ jobId: jobId }));
    setIsLoading(false);
    _loadData();
  };
  const onAddShift = async (data) => {
    const params = {
      ...initializeData,
      id: jobId,
      isPostJob: initializeData.isPostJob === POST_JOB.POST_JOB ? true : false,
      jobAvailabilityRequests: [
        ...initializeData.jobAvailabilityRequests,
        data,
      ],
    };
    await _submitUpdate(params);
    _loadData();
  };

  const _submitCreate = async (data) => {
    let newData = { ...data };
    setSubmitting(true);
    const result = await dispatch(reduceCreateJob(newData));
    setSubmitting(false);
    if (result.payload) {
      history.push(`detail/${result.payload.id}`);
    }
  };

  const _submitUpdate = async (data) => {
    setSubmitting(true);
    // await dispatch(reduceUpdateJob({ ...data }));
    _loadData();
    setSubmitting(false);
  };
  const handleSubmit = (data) => {
    const params = {
      ...data,
      isPostJob: initializeData.isPostJob === POST_JOB.POST_JOB ? true : false,
      yearsOfExperience:
        data.yearsOfExperience === "" ? 0 : data.yearsOfExperience,
      lat,
      lng,
    };
    if (!isAdd) {
      params["id"] = jobId;
    }
    if (isAdd) {
      _submitCreate(params);
    } else {
      _submitUpdate(params);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadClientDetail();
    }
  }, [clientId]);
  useEffect(() => {
    isHospital ? loadSpecialties() : loadSupports();
    loadAccreditations(WORKER_TYPE.NURSE);
    if (!isAdd && !!jobId) {
      _loadData();
      loadWorkersApplied();
    } else {
      isCoordinator && loadParticipants();
    }
  }, [jobId]);
  useEffect(() => {
    if (isAdd && clientDetail && !isCoordinator) {
      const cloneInit = { ...initializeData };
      const { client: clientInfo = {} } = clientDetail;
      cloneInit["address1"] = clientInfo.address1;
      cloneInit["address2"] = clientInfo.address2;
      cloneInit["postalCode"] = clientInfo.postalCode;
      cloneInit["phone"] = clientInfo.phone;
      setLatLng({
        lat: clientInfo.lat,
        lng: clientInfo.lng,
      });
      setInitializeData(cloneInit);
    }
  }, [clientDetail]);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchProcessNotePage({ jobId: jobId }));
    }
  }, [jobId]);

  const shiftValidate = Yup.object().shape(
    {
      startTime: Yup.date().when("status", (status) => {
        if (!status || status !== "INACTIVE") {
          return Yup.date()
            .required("validation.required")
            .when("endTime", (endTime, schema) => {
              if (endTime && endTime !== "") {
                return schema.max(
                  moment(endTime)
                    .subtract(1, "minutes")
                    .format("yyyy/MM/DD HH:mm"),
                  "validation.startTimeMustBeBeforeEndTime"
                );
              }
            });
        }
      }),
      endTime: Yup.date().when("status", (status) => {
        if (!status || status !== "INACTIVE") {
          return Yup.date()
            .required("validation.required")
            .when("startTime", (startTime, schema) => {
              if (startTime && startTime !== "") {
                return schema.min(
                  moment(startTime)
                    .add(1, "minutes")
                    .format("yyyy/MM/DD HH:mm"),
                  "validation.endTimeMustBeAfterStartTime"
                );
              }
            });
        }
      }),
      shiftType: Yup.string().when("status", (status) => {
        if (!status || status !== "INACTIVE") {
          return Yup.string().required("validation.required");
        }
      }),
      numberWorkers: Yup.number().when("status", (status) => {
        if (!status || status !== "INACTIVE") {
          return Yup.number()
            .required("validation.required")
            .min(1, { message: "validation.minValue", value: 1 })
            .max(100, { message: "validation.maxValue", value: 100 })
            // .test(
            //   "len",
            //   "validation.max_length_10",
            //   (val) => Math.ceil(Math.log10(val + 1)) <= 10
            // );
        }
      }),
    },
    [
      ["startTime", "endTime"],
      ["endTime", "startTime"],
    ]
  );
  const validationSchema = Yup.object().shape({
    title: YupValidations.requiredString.max(64, "validation.max_length_64"),
    description: Yup.string()
      .trim()
      .required("validation.required")
      .max(1000, "validation.max_length_1000"),
    address1: YupValidations.requiredString,
    address2: Yup.string().max(240, "validation.max_length_240"),
    phone: YupValidations.phone,
    postalCode: YupValidations.postalCode,
    accreditationId: isHospital && Yup.string().required("validation.required"),
    specialtyId: isHospital && Yup.string().required("validation.required"),
    supportId: !isHospital && Yup.string().required("validation.required"),
    coordParticipantId:
      isCoordinator && Yup.string().trim().required("validation.required"),
    yearsOfExperience: Yup.number()
      .min(0, { message: "validation.minValue", value: 0 })
      .integer()
      .nullable(true)
      .max(99, { message: "validation.maxValue", value: 99 }),
      // .test(
      //   "len",
      //   "validation.max_length_10",
      //   (val) => val !== "" || Math.ceil(Math.log10(val + 1)) <= 10
      // ),
    startDate: Yup.date().required("validation.required"),
    endDate: Yup.date().required("validation.required")
    .when('startDate', (startDate, schema) => (startDate && schema.min(moment(startDate).format("YYYY/MM/DD HH:mm"), 'validation.endDateMustBeGreaterThanStartDate'))),
    jobAvailabilityRequests: Yup.array().of(shiftValidate),
    department: Yup.string().nullable().max(255, "validation.max_length_255"),
    floor: Yup.string().nullable().max(255, "validation.max_length_255")
  });

  return (
    <React.Fragment>
      <Formik
        initialValues={initializeData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, values, handleChange }) => (
          <Form>
            {isCoordinator && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <Box
                    className={classNames({
                      [classes.title]: true,
                      "mt-0": true,
                    })}
                  >
                    <Typography variant="h5">
                      {t("title.participant")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    component={SelectField}
                    name={"coordParticipantId"}
                    label={"label.name"}
                    options={participants.map((item) => {
                      return {
                        value: item.id,
                        label: `${item.firstName} ${item.lastName} (${item.email})`,
                      };
                    })}
                    onSelected={(selectedId) => {
                      const selectedParticipant = participants.filter(
                        (item) => item.id === selectedId
                      )[0];
                      if (selectedParticipant) {
                        setFieldValue("address1", selectedParticipant.address1);
                        setFieldValue("address2", selectedParticipant.address2);
                        setFieldValue(
                          "postalCode",
                          selectedParticipant.postalCode
                        );
                        setFieldValue("phone", selectedParticipant.phone);

                        setFieldValue(
                          "billingDetail",
                          selectedParticipant.billingDetail
                        );
                        setFieldValue(
                          "billToAddress",
                          selectedParticipant.billToAddress
                        );

                        setLatLng({
                          lat: selectedParticipant.lat,
                          lng: selectedParticipant.lng,
                        });
                        if (
                          selectedParticipant.billingDetail ===
                          BILLING_TYPE.PLAN_MANAGER.id
                        ) {
                          setFieldValue(
                            "planManagerOrganizationName",
                            selectedParticipant.planManagerOrganizationName
                          );
                          setFieldValue(
                            "planManagerPhone",
                            selectedParticipant.planManagerPhone
                          );
                          setFieldValue(
                            "planManagerEmail",
                            selectedParticipant.planManagerEmail
                          );
                        }
                      }
                    }}
                    required
                    disabled={workersApplied.length > 0}
                  />
                  {values.billingDetail === BILLING_TYPE.PLAN_MANAGER.id && (
                    <React.Fragment>
                      <Field
                        component={AppInput}
                        name="planManagerOrganizationName"
                        label={"label.organizationName"}
                        disabled={true}
                      />
                      <Field
                        component={AppInput}
                        name="planManagerPhone"
                        type="tel"
                        label={"label.phoneNumber"}
                        disabled={true}
                      />
                    </React.Fragment>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    component={SelectField}
                    label={t("label.billingDetails")}
                    placeholder={t("label.billingDetails")}
                    name={"billingDetail"}
                    options={billingDetailsOptions}
                    disabled={true}
                  />
                  <Field
                    component={AppInput}
                    name="billToAddress"
                    id="billToAddress"
                    label={"label.billToAddress"}
                    disabled={true}
                  />
                  {values.billingDetail === BILLING_TYPE.PLAN_MANAGER.id && (
                    <React.Fragment>
                      <Field
                        component={AppInput}
                        name="planManagerEmail"
                        label={"label.email"}
                        disabled={true}
                      />
                    </React.Fragment>
                  )}
                </Grid>
              </Grid>
            )}
            <Grid container spacing={3}>
              {isCoordinator && (
                <React.Fragment>
                  <Grid item xs={12} md={12}>
                    <Box className={classes.title}>
                      <Typography variant="h5">
                        {t("title.jobInformation")}
                      </Typography>
                    </Box>
                  </Grid>
                </React.Fragment>
              )}
              <Grid item xs={12} md={6}>
                <Field
                  component={AppInput}
                  name="title"
                  label={t("label.title")}
                  required
                />
                <Field
                  component={AppInput}
                  name="description"
                  label={t("label.description")}
                  type="textarea"
                  required
                />
                <Field
                  component={InputAutocompleteAddress}
                  name="address1"
                  label={"label.address1"}
                  onChangePostalCode={(postalcode) =>
                    setFieldValue("postalCode", postalcode)
                  }
                  onChangeLatLng={(latlng) => setLatLng(latlng)}
                  required
                />
                <Field
                  component={AppInput}
                  name="address2"
                  label={t("label.address2")}
                />
                <Field
                  component={AppInput}
                  name="postalCode"
                  type="tel"
                  mask={MASK.PHONE}
                  maskChar=""
                  label={"label.postalCode"}
                  required
                />
                {isHospital && (
                  <React.Fragment>
                    <Field
                      component={AppInput}
                      name="department"
                      label={t("label.department")}
                    />
                    <Field
                      component={AppInput}
                      name="floor"
                      label={t("label.floor")}
                    />
                  </React.Fragment>
                )}
                <Field
                  component={AppInput}
                  name="phone"
                  label={t("label.phoneNumber")}
                  required
                  mask={MASK.PHONE}
                  maskChar=""
                  type="tel"
                />
                {!isHospital && (
                  <Field
                    component={AppInput}
                    name="yearsOfExperience"
                    label={t("title.yearsOfExperience")}
                    type="number"
                    disabled={workersApplied.length > 0}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {isHospital ? (
                  <React.Fragment>
                    <Field
                      component={SelectField}
                      name="specialtyId"
                      label={t("label.specialty")}
                      placeholder={t("label.specialty")}
                      options={specialtyOptions}
                      disabled={workersApplied.length > 0}
                      required
                    />
                    <Field
                      component={SelectField}
                      name="accreditationId"
                      label={t("label.accreditation")}
                      placeholder={t("label.accreditation")}
                      options={accreditations.map((item) => {
                        return {
                          value: item.id,
                          label: item.name,
                        };
                      })}
                      disabled={workersApplied.length > 0}
                      required
                    />
                  </React.Fragment>
                ) : (
                  <Field
                    component={SelectField}
                    name="supportId"
                    label={t("label.support")}
                    placeholder={t("label.support")}
                    options={supportOptions}
                    disabled={workersApplied.length > 0}
                    required
                  />
                )}
                {isHospital && (
                  <Field
                    component={AppInput}
                    name="yearsOfExperience"
                    label={t("title.yearsOfExperience")}
                    type="number"
                    disabled={workersApplied.length > 0}
                  />
                )}
                <Box className={classes.title}>
                  <Typography variant="h5">
                    {t("title.availability")}
                    <span style={{ color: AppColor.red }}>*</span>
                  </Typography>
                </Box>
                <Field
                  component={CustomDatePicker}
                  name="startDate"
                  label={"label.startDate"}
                  required
                  formatDateJob={true}
                  custom={{
                    minDate: new Date(
                      moment(new Date()).startOf("day").add(1, "days")
                    ),
                  }}
                  disabled={totalBooking > 0}
                />
                <Field
                  component={CustomDatePicker}
                  name="endDate"
                  label={"label.endDate"}
                  required
                  formatDateJob={true}
                  custom={{
                    minDate: new Date(
                      moment(
                        values.startDate
                          ? new Date(values.startDate)
                          : new Date()
                      )
                        .startOf("day")
                        .add(1, "days")
                    ),
                  }}
                  disabled={totalBooking > 0}
                />
                {jobType !== JOB_TYPE.SINGLE_WORKER && (
                  <React.Fragment>
                    <FieldArray
                      name="jobAvailabilityRequests"
                      render={({ insert, remove, replace }) => (
                        <React.Fragment>
                          <Box className="mb-3">
                            <AppButton
                              className="btn btn-info mr-1"
                              mode="outlined"
                              buttonColor={AppColor.mainBlue}
                              type="button"
                              label={t("button.add")}
                              onClick={() => {
                                insert(values.jobAvailabilityRequests.length, {
                                  ...defaultWorkHistory,
                                });
                              }}
                            >
                              <i className="fas fa-plus"></i>
                            </AppButton>
                          </Box>
                          {values.jobAvailabilityRequests &&
                            values.jobAvailabilityRequests.map(
                              (item, index) => {
                                const {
                                  dayOfWeek,
                                  shiftType,
                                  startTime,
                                  endTime,
                                  isExpand,
                                  numberOfBooked = 0,
                                  totalBookings = 0,
                                  id: shiftId,
                                } = values.jobAvailabilityRequests[index];

                                const availabilitySameDay = values.jobAvailabilityRequests.filter(
                                  (item) =>
                                    item.dayOfWeek === dayOfWeek &&
                                    item.status !== "INACTIVE"
                                );
                                const existedShifts = availabilitySameDay.map(
                                  (item) => item.shiftType
                                );

                                let maxTime,
                                  maxHours = 23,
                                  maxMinutes = 59,
                                  minTime,
                                  minHours = 6,
                                  minMinutes = 0,
                                  isFullTimeSlot;

                                switch (shiftType) {
                                  case "MORNING":
                                    maxTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "AFTERNOON"
                                    )[0];
                                    if (!maxTime) {
                                      maxTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "EVENING"
                                      )[0];
                                    }
                                    if (!maxTime) {
                                      maxTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "NIGHT"
                                      )[0];
                                    }
                                    break;
                                  case "AFTERNOON":
                                    minTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "MORNING"
                                    )[0];

                                    maxTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "EVENING"
                                    )[0];
                                    if (!maxTime) {
                                      maxTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "NIGHT"
                                      )[0];
                                    }
                                    break;
                                  case "EVENING":
                                    minTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "AFTERNOON"
                                    )[0];
                                    if (!minTime) {
                                      minTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "MORNING"
                                      )[0];
                                    }

                                    maxTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "NIGHT"
                                    )[0];
                                    break;
                                  case "NIGHT":
                                    minTime = availabilitySameDay.filter(
                                      (item) => item.shiftType === "EVENING"
                                    )[0];
                                    if (!minTime) {
                                      minTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "AFTERNOON"
                                      )[0];
                                    }
                                    if (!minTime) {
                                      minTime = availabilitySameDay.filter(
                                        (item) => item.shiftType === "MORNING"
                                      )[0];
                                    }
                                    if (
                                      minTime &&
                                      moment(minTime.endTime).hours() < 12
                                    ) {
                                      minTime["endTime"] = moment(
                                        minTime.endTime
                                      )
                                        .hours(12)
                                        .minutes(0)
                                        .format("YYYY/MM/DD HH:mm");
                                    }
                                    if (!minTime) {
                                      minTime = {
                                        endTime: moment(new Date())
                                          .hours(12)
                                          .minutes(0)
                                          .format("YYYY/MM/DD HH:mm"),
                                      };
                                    }

                                    if (moment(endTime).hours() === 6) {
                                      maxMinutes = 0;
                                    }
                                    break;
                                  default:
                                    break;
                                }

                                if (minTime) {
                                  minHours = moment(minTime.endTime).hours();
                                  if (moment(minTime.endTime).hours() === 23) {
                                    if (
                                      moment(minTime.endTime).minutes() >= 55
                                    ) {
                                      if (shiftType !== "NIGHT") {
                                        isFullTimeSlot = true;
                                      } else {
                                        minHours = 23;
                                      }
                                    }
                                  }

                                  minMinutes = moment(
                                    minTime.endTime
                                  ).minutes();
                                  if (
                                    (moment(startTime).hours() ||
                                      moment(startTime).hours() === 0) &&
                                    moment(startTime).hours() !==
                                      moment(minTime.endTime).hours()
                                  ) {
                                    minMinutes = 0;
                                  }
                                }
                                if (maxTime) {
                                  maxHours = moment(maxTime.startTime).hours();
                                  maxMinutes = moment(
                                    maxTime.startTime
                                  ).minutes();
                                  if (
                                    moment(endTime).hours() !==
                                    moment(maxTime.startTime).hours()
                                  ) {
                                    maxMinutes = 59;
                                  }
                                }

                                if (item.status && item.status === "INACTIVE") {
                                  return;
                                }
                                return (
                                  <Box
                                    key={`ip-${index}`}
                                    className={
                                      isExpand
                                        ? classes.wrapShift
                                        : classes.collapseShift
                                    }
                                  >
                                    <Box
                                      className={"shiftInfo"}
                                      onClick={() =>
                                        setFieldValue(
                                          `jobAvailabilityRequests[${index}].isExpand`,
                                          !isExpand
                                        )
                                      }
                                    >
                                      <span>
                                        {`${t(`daysOfTheWeek.${dayOfWeek}`)}, ${
                                          shiftType
                                            ? t(`shift.${shiftType}`)
                                            : ""
                                        } ${
                                          startTime
                                            ? moment(startTime).format("HH:mm")
                                            : "00:00"
                                        } - ${
                                          endTime
                                            ? moment(endTime).format("HH:mm")
                                            : "00:00"
                                        }`}
                                      </span>
                                      <span>
                                        <Chip
                                          style={{
                                            backgroundColor: AppColor.pink,
                                            color: AppColor.white,
                                          }}
                                          size="small"
                                          label={`${numberOfBooked}/${totalBookings}`}
                                        />
                                      </span>
                                    </Box>
                                    {isExpand ? (
                                      <React.Fragment>
                                        <Field
                                          component={SelectField}
                                          name={`jobAvailabilityRequests[${index}].dayOfWeek`}
                                          label={"label.daysOfTheWeek"}
                                          options={DAYS_OF_WEEK.map((item) => {
                                            return {
                                              value: item,
                                              label: t(`daysOfTheWeek.${item}`),
                                            };
                                          })}
                                          disabled={
                                            !!numberOfBooked &&
                                            numberOfBooked > 0
                                          }
                                          onSelected={() => {
                                            setFieldValue(
                                              `jobAvailabilityRequests[${index}].shiftType`,
                                              ""
                                            );

                                            setFieldValue(
                                              `jobAvailabilityRequests[${index}].startTime`,
                                              moment(new Date())
                                                .hours(0)
                                                .minutes(0)
                                                .format("YYYY/MM/DD HH:mm")
                                            );
                                            setFieldValue(
                                              `jobAvailabilityRequests[${index}].endTime`,
                                              moment(new Date())
                                                .hours(0)
                                                .minutes(0)
                                                .format("YYYY/MM/DD HH:mm")
                                            );
                                          }}
                                          required
                                        />
                                        <Field
                                          component={SelectField}
                                          name={`jobAvailabilityRequests[${index}].shiftType`}
                                          label={"label.shift"}
                                          options={SHIFT.filter(
                                            (exis) =>
                                              exis === shiftType ||
                                              !existedShifts.includes(exis)
                                          ).map((item) => {
                                            return {
                                              value: item,
                                              label: t(`shift.${item}`),
                                            };
                                          })}
                                          disabled={
                                            !!numberOfBooked &&
                                            numberOfBooked > 0
                                          }
                                          onSelected={() => {
                                            setFieldValue(
                                              `jobAvailabilityRequests[${index}].startTime`,
                                              ""
                                            );
                                            setFieldValue(
                                              `jobAvailabilityRequests[${index}].endTime`,
                                              ""
                                            );
                                          }}
                                          required
                                        />
                                        {shiftType && !isFullTimeSlot && (
                                          <Box>
                                            <Label
                                              className={`${classes.label}`}
                                              style={{ color: AppColor.pink }}
                                            >
                                              <span>Valid time: </span>
                                              <span>{`${
                                                minTime
                                                  ? moment(
                                                      minTime.endTime
                                                    ).format("HH:mm")
                                                  : "06:00"
                                              } - ${
                                                maxTime
                                                  ? moment(
                                                      maxTime.startTime
                                                    ).format("HH:mm")
                                                  : shiftType !== "NIGHT"
                                                  ? "23:55"
                                                  : "06:00 next day"
                                              }`}</span>
                                            </Label>
                                          </Box>
                                        )}
                                        {isFullTimeSlot && (
                                          <Label
                                            className={`${classes.label}`}
                                            style={{ color: AppColor.red }}
                                          >
                                            {t("text.fullTimeSlot", {
                                              value: t(`shift.${shiftType}`),
                                            })}
                                          </Label>
                                        )}
                                        {shiftType !== "NIGHT" ? (
                                          <React.Fragment>
                                            <Field
                                              component={SelectTime}
                                              name={`jobAvailabilityRequests[${index}].startTime`}
                                              label={"label.startTime"}
                                              minTime={moment()
                                                .hours(minHours)
                                                .minutes(minMinutes)
                                                .format("HH:mm")}
                                              maxTime={moment()
                                                .hours(maxHours)
                                                .minutes(maxMinutes)
                                                .format("HH:mm")}
                                              disabled={
                                                (!!numberOfBooked &&
                                                  numberOfBooked > 0) ||
                                                !shiftType ||
                                                shiftType === "" ||
                                                isFullTimeSlot
                                              }
                                              required
                                            />
                                            <Field
                                              component={SelectTime}
                                              name={`jobAvailabilityRequests[${index}].endTime`}
                                              label={"label.endTime"}
                                              minTime={moment()
                                                .hours(minHours)
                                                .minutes(minMinutes)
                                                .format("HH:mm")}
                                              maxTime={moment()
                                                .hours(maxHours)
                                                .minutes(maxMinutes)
                                                .format("HH:mm")}
                                              disabled={
                                                (!!numberOfBooked &&
                                                  numberOfBooked > 0) ||
                                                !shiftType ||
                                                shiftType === "" ||
                                                isFullTimeSlot
                                              }
                                              required
                                            />
                                          </React.Fragment>
                                        ) : (
                                          <React.Fragment>
                                            <Field
                                              component={SelectTime}
                                              name={`jobAvailabilityRequests[${index}].startTime`}
                                              label={"label.startTime"}
                                              disabled={
                                                (!!numberOfBooked &&
                                                  numberOfBooked > 0) ||
                                                !shiftType ||
                                                shiftType === "" ||
                                                isFullTimeSlot
                                              }
                                              minTime={moment()
                                                .hours(0)
                                                .minutes(minMinutes)
                                                .format("HH:mm")}
                                              shouldDisableTime={({timeValue,clockType}) => {
                                                if (
                                                  clockType === "hours" &&
                                                  (timeValue >= minHours ||
                                                    timeValue <= 6)
                                                ) {
                                                  return true;
                                                }
                                                if (
                                                  clockType === "minutes" &&
                                                  (timeValue >= minMinutes ||
                                                    timeValue <= 59)
                                                ) {
                                                  return true;
                                                }
                                                return false;
                                              }}
                                              isSleepover
                                              required
                                            />
                                            <Field
                                              component={SelectTime}
                                              name={`jobAvailabilityRequests[${index}].endTime`}
                                              label={"label.endTime"}
                                              minTime={moment()
                                                .hours(0)
                                                .minutes(0)
                                                .format("HH:mm")}
                                              maxTime={moment()
                                                .hours(6)
                                                .minutes(maxMinutes)
                                                .format("HH:mm")}
                                              disabled={
                                                (!!numberOfBooked &&
                                                  numberOfBooked > 0) ||
                                                !shiftType ||
                                                shiftType === "" ||
                                                isFullTimeSlot
                                              }
                                              isSleepover
                                              required
                                            />
                                          </React.Fragment>
                                        )}
                                        <Field
                                          component={AppInput}
                                          name={`jobAvailabilityRequests[${index}].numberWorkers`}
                                          label={"label.numberOfStaffs"}
                                          type="tel"
                                          mask={maskNumber}
                                          maskChar=""
                                          required
                                          disabled={
                                            !!numberOfBooked &&
                                            numberOfBooked > 0
                                          }
                                        />
                                      </React.Fragment>
                                    ) : (
                                      <React.Fragment>
                                        {values.jobAvailabilityRequests.length >
                                          0 && (
                                          <Box className={"remove-icon"}>
                                            <Box
                                              onClick={() => {
                                                if (shiftId) {
                                                  const newValue = {
                                                    ...values
                                                      .jobAvailabilityRequests[
                                                      index
                                                    ],
                                                    status: "INACTIVE",
                                                  };
                                                  replace(index, newValue);
                                                } else {
                                                  remove(index);
                                                }
                                              }}
                                              className={classNames({
                                                [classes.deleteRight]: true,
                                                [classes.disabled]:
                                                  !!numberOfBooked &&
                                                  numberOfBooked > 0,
                                              })}
                                            >
                                              <i className="far fa-times-circle"></i>
                                            </Box>
                                          </Box>
                                        )}
                                      </React.Fragment>
                                    )}
                                  </Box>
                                );
                              }
                            )}
                        </React.Fragment>
                      )}
                    />
                    <RadioGroup
                      aria-label="Post Job"
                      name="isPostJob"
                      value={values.isPostJob}
                      onChange={handleChange("isPostJob")}
                      className={classes.radioGroup}
                    >
                      <FormControlLabel
                        value={POST_JOB.POST_JOB}
                        control={<AppRadio />}
                        label={t("label.postJob")}
                        className={classes.radio}
                      />
                      <FormControlLabel
                        value={POST_JOB.UNPOST_JOB}
                        control={<AppRadio />}
                        label={t("label.unPostJob")}
                        className={classes.radio}
                      />
                    </RadioGroup>
                    <Box className={classes.postDescription}>
                      <Box>
                        <span className={classes.bold}>
                          {t("label.postJob")}
                        </span>
                        <span>{` ${t("text.postJob")}`}</span>
                      </Box>
                      <Box>
                        <span className={classes.bold}>
                          {t("label.unPostJob")}
                        </span>
                        <span>{` ${t("text.unPostJob")}`}</span>
                      </Box>
                    </Box>
                  </React.Fragment>
                )}
              </Grid>
            </Grid>
            <FormGroup className={classes.control}>
              <AppButton
                label={`${t("button.back")}`}
                mode="outlined"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                onClick={() => history.push("/jobs")}
              />
              <AppButton
                className="ml-1"
                label={`${t("button.save")}`}
                rounder
                type="submit"
                isLoading={isSubmitting || isLoading}
                disabled={isSubmitting || isLoading ||(jobDetail && jobDetail.jobStatus === JOB_STATUS.DONE)}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>
      <Box className="mb-3">
        {!isAdd && jobType === JOB_TYPE.MULTIPLE_WORKER && (
          <React.Fragment>
            <AppButton
              className="mr-1"
              label={
                isHospital
                  ? t("label.searchNurse")
                  : t("label.searchSupportWorker")
              }
              rounder
              onClick={() => setShowSearchWorker(true)}
              isLoading={isLoading}
              disabled={isLoading}
              buttonColor={AppColor.turquoise}
            />
            <AppButton
              className="mr-1"
              label={`${t("label.booking")}`}
              rounder
              buttonColor={AppColor.pink}
              onClick={() => setShowBookingWorker(true)}
              isLoading={isLoading}
              disabled={isLoading}
            />
            <AppButton
              className="mr-1"
              label={`${t("label.autoBooking")}`}
              rounder
              buttonColor={AppColor.mainColor}
              onClick={() => handleAutoBooking()}
              isLoading={isLoading}
              disabled={isLoading}
            />
          </React.Fragment>
        )}
        {!isAdd && (
          <img
            onClick={() => setShowCalendar(true)}
            src={CalendarIcon}
            className={classes.calendar}
          />
        )}
      </Box>
      <Grid container spacing={3}>
        {workersApplied.map((item, index) => (
          <Grid item xs={12} sm={12} md={6} key={`appiled-worker-${index}`}>
            <JobUserTag
              isApplied
              userInfo={item.worker}
              onRemove={handleRemoveWorker}
              isBooked={item.applyStatus === BOOKING_STATUS.BOOKED}
            />
          </Grid>
        ))}
      </Grid>
      <AppDialog
        open={showSearchWorker}
        onClose={() => setShowSearchWorker(false)}
        maxWidth="sm"
        title={
          isHospital ? t("label.searchNurse") : t("label.searchSupportWorker")
        }
      >
        <SearchWorkerModal
          jobId={jobId}
          loadWorkersApplied={loadWorkersApplied}
        />
      </AppDialog>
      <AppDialog
        open={showBookingWorker}
        onClose={() => {
          _loadData();
          loadWorkersApplied();
          setShowBookingWorker(false);
        }}
        maxWidth="sm"
        title={t("label.booking")}
      >
        <BookingWorkerModal
          jobId={jobId}
          jobDetail={initializeData}
          onAddShift={onAddShift}
        />
      </AppDialog>
      <AppDialog
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        maxWidth="md"
        title={t("label.calendar")}
      >
        <WorkerCalendarModal jobId={jobId} jobDetail={jobDetail} />
      </AppDialog>
      {!isAdd && <ProcessNoteContent jobId={jobId} jobStatus={jobDetail.jobStatus} processNote={processNote}></ProcessNoteContent>}
    </React.Fragment>
  );
};

JobForm.propTypes = {
  isAdd: PropTypes.bool,
  isAdmin: PropTypes.bool,
  jobId: PropTypes.any,
  onCancel: PropTypes.func,
  clientDetail: PropTypes.object,
};

export default JobForm;